import type { Sale, Snapshot } from './data';
import { type Filters, type Model, channelGroup, days, ratio, sum, totals } from './model';

const CUSTOMER_REGIONS: Record<string, string> = {
  China: 'Asia',
  'Hong Kong': 'Asia',
  Indonesia: 'Asia',
  Japan: 'Asia',
  Macao: 'Asia',
  Malaysia: 'Asia',
  Philippines: 'Asia',
  Singapore: 'Asia',
  'South Korea': 'Asia',
  'Taiwan (Province of China)': 'Asia',
  Thailand: 'Asia',
  Vietnam: 'Asia',
  France: 'Europe',
  Germany: 'Europe',
  Ireland: 'Europe',
  Italy: 'Europe',
  Netherlands: 'Europe',
  Spain: 'Europe',
  'United Kingdom': 'Europe',
  Canada: 'North America',
  'United States': 'North America',
  Israel: 'Rest of the World',
  Qatar: 'Rest of the World',
  'Russian Federation': 'Rest of the World',
  'Saudi Arabia': 'Rest of the World',
  'South Africa': 'Rest of the World',
  'United Arab Emirates': 'Rest of the World',
};

// Source estimated_adults defaults to two only when all guest counts sum to zero.
export function guestGroup(row: Sale): string {
  const total = (row.number_of_adults ?? 0) + (row.number_of_children ?? 0) + (row.number_of_infants ?? 0);
  const adults = total === 0 ? 2 : (row.number_of_adults ?? 0);
  if (adults === 1) {
    return 'Solo';
  }
  if (
    adults === 2 &&
    row.number_of_children !== null &&
    row.number_of_infants !== null &&
    row.number_of_children + row.number_of_infants === 0
  ) {
    return 'Couples';
  }
  return adults >= 2 ? 'Families & Groups' : 'Other';
}
export function subRegion(row: Sale): string {
  if (!['AUSTRALIA', 'AU', 'AUS'].includes((row.country_name || '').trim().toUpperCase())) {
    return ' ';
  }
  const state = row.customer_geo_state || '';
  const patterns: Array<[string, RegExp]> = [
    ['NSW', /\bNEW\b\s*\bSOUTH\b\s*\bWALES\b|\bNSW\b|N\s*S\s*W/],
    ['VIC', /\bVICTORIA\b|\bVIC\b|V\s*I\s*C/],
    ['QLD', /\bQUEENSLAND\b|\bQLD\b|Q\s*L\s*D/],
    ['SA', /\bSOUTH\b\s*\bAUSTRALIA\b|\bSA\b|S\s*A/],
    ['WA', /\bWESTERN\b\s*\bAUSTRALIA\b|\bWA\b|W\s*A/],
    ['TAS', /\bTASMANIA\b|\bTAS\b|T\s*A\s*S/],
    ['ACT', /\bAUSTRALIAN\b\s*\bCAPITAL\b\s*\bTERRITORY\b|\bACT\b|A\s*C\s*T/],
    ['NT', /\bNORTHERN\b\s*\bTERRITORY\b|\bNT\b|N\s*T/],
  ];
  return patterns.find(([, pattern]) => pattern.test(state))?.[0] || 'Other (AU)';
}
export function buildDetails(snapshot: Snapshot, filters: Filters, model: Model) {
  function groups(key: (row: Sale) => string) {
    const names = [...new Set([...model.current.rows, ...model.comparison.rows].map(key))].sort();
    return names.map((name) => ({
      name,
      current: totals(
        model.current.rows.filter((row) => key(row) === name),
        [],
        filters,
      ),
      comparison: totals(
        model.comparison.rows.filter((row) => key(row) === name),
        [],
        filters,
      ),
    }));
  }
  const guestMix = groups(guestGroup),
    roomGuestMix = groups((row) => `${row.room_type_name || 'Unknown'} · ${guestGroup(row)}`),
    customerDetails = groups(
      (row) =>
        `${CUSTOMER_REGIONS[row.country_name || ''] || row.country_name || 'Unknown'} · ${row.country_name || 'Unknown'} · ${subRegion(row)}`,
    );
  function months(rows: Array<Sale>, referenceRows: Array<Sale>) {
    const checkins = referenceRows
      .map((row) => row.check_in_date)
      .filter((date): date is string => !!date)
      .sort();
    const first = checkins[0];
    if (!first) {
      return [];
    }
    const firstMonth = Number(first.slice(0, 4)) * 12 + Number(first.slice(5, 7));
    const selected = rows.filter((row) => {
      if (!row.check_in_date) {
        return false;
      }
      const month = Number(row.check_in_date.slice(0, 4)) * 12 + Number(row.check_in_date.slice(5, 7));
      return month >= firstMonth && month < firstMonth + 18;
    });
    const names = [...new Set(selected.map((row) => row.check_in_date.slice(0, 7)))].sort();
    return names.map((month) => {
      const scoped = selected.filter((row) => row.check_in_date.startsWith(month));
      return {
        name: `Year ${Number(month.slice(0, 4)) - Number(first.slice(0, 4)) + 1} · ${new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' }).format(new Date(month + '-01T00:00:00Z'))}`,
        calendarMonth: month,
        totals: totals(scoped, [], filters),
        afterRefund: totals(scoped, [], { ...filters, refund: 'After Refund' }),
      };
    });
  }
  const currentReference = snapshot.sales.filter(
    (row) => row.offer_bk === filters.current && row.date >= filters.from && row.date <= filters.to,
  );
  const comparisonReference = snapshot.sales.filter(
    (row) => row.offer_bk === filters.comparison && row.date >= filters.cmpFrom && row.date <= filters.cmpTo,
  );
  const currentMonths = months(model.current.rows, currentReference),
    comparisonMonths = months(model.comparison.rows, comparisonReference);
  const monthNames = [...new Set([...currentMonths, ...comparisonMonths].map((row) => row.name))].sort(
    (a, b) =>
      Number(a.split(' ')[1]) - Number(b.split(' ')[1]) ||
      ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(a.slice(-3)) -
        ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(b.slice(-3)),
  );
  const monthRows = monthNames.map((name) => ({
    name,
    current: currentMonths.find((row) => row.name === name),
    comparison: comparisonMonths.find((row) => row.name === name),
  }));
  // Month (2) uses a check-in-period classifier, unlike the transaction-period revenue chart.
  // Preserve its explicit NA group rather than silently reclassifying future check-ins.
  const classedRows: Array<{ name: string; row: Sale; side: 'current' | 'comparison' }> = [];
  for (const [side, rows, referenceRows] of [
    ['current', model.current.rows, currentReference],
    ['comparison', model.comparison.rows, comparisonReference],
  ] as const) {
    const years = referenceRows
      .map((row) => row.check_in_date?.slice(0, 4))
      .filter((year): year is string => !!year)
      .sort();
    if (!years.length) {
      continue;
    }
    for (const row of rows) {
      if (!row.check_in_date) {
        continue;
      }
      const year = Number(row.check_in_date.slice(0, 4)) - Number(years[0]) + 1;
      if (year < 1 || year > 2) {
        continue;
      }
      const current =
        row.offer_opportunity_name === filters.current &&
        row.check_in_date >= filters.from &&
        row.check_in_date <= filters.to;
      const comparison =
        row.offer_opportunity_name === filters.comparison &&
        row.check_in_date >= filters.cmpFrom &&
        row.check_in_date <= filters.cmpTo;
      const period = current && !comparison ? 'Current Period' : comparison && !current ? 'Comparison Period' : 'NA';
      classedRows.push({
        side,
        name: `Year ${year} · ${new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' }).format(new Date(row.check_in_date + 'T00:00:00Z'))} · ${period}`,
        row,
      });
    }
  }
  const checkinClassRows = [...new Set(classedRows.map((value) => value.name))]
    .sort(
      (a, b) =>
        Number(a.split(' ')[1]) - Number(b.split(' ')[1]) ||
        ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(
          a.split(' · ')[1],
        ) -
          ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(
            b.split(' · ')[1],
          ) ||
        a.localeCompare(b),
    )
    .map((name) => ({
      name,
      current: totals(
        classedRows.filter((value) => value.name === name && value.side === 'current').map((value) => value.row),
        [],
        { ...filters, refund: 'After Refund' },
      ).orders,
      comparison: totals(
        classedRows.filter((value) => value.name === name && value.side === 'comparison').map((value) => value.row),
        [],
        { ...filters, refund: 'After Refund' },
      ).orders,
      value: totals(
        classedRows.filter((value) => value.name === name).map((value) => value.row),
        [],
        { ...filters, refund: 'After Refund' },
      ).orders,
    }));
  const roomShares = model.rooms.map((room) => ({
    ...room,
    currentShare: ratio(
      room.current.packages,
      sum(model.rooms, (row) => row.current.packages),
    ),
    comparisonShare: ratio(
      room.comparison.packages,
      sum(model.rooms, (row) => row.comparison.packages),
    ),
  }));
  const regionField =
    filters.region === 'Country'
      ? 'country_match'
      : filters.region === 'Area Level 1'
        ? 'area1_match'
        : filters.region === 'Area Level 2'
          ? 'area2_match'
          : 'territory_match';
  const viewRegionField =
    filters.region === 'Country' ? 'country_match' : filters.region === 'Area Level 1' ? 'area1_match' : 'area2_match';
  function region(opportunity: string, from: string, to: string) {
    const rows = (snapshot.regionSales || []).filter((row) => row[regionField] && row.date >= from && row.date <= to);
    const known = rows.length > 0 && rows.every((row) => Array.isArray(row.order_keys));
    const denominator = new Set(rows.flatMap((row) => row.order_keys || [])).size;
    const numerator = new Set(
      rows.filter((row) => row.offer_opportunity_name === opportunity).flatMap((row) => row.order_keys || []),
    ).size;
    const views = (snapshot.regionViews || []).filter(
      (row) => row[viewRegionField] && row.date >= from && row.date <= to,
    );
    return {
      bookings: known ? ratio(numerator, denominator) : null,
      views: ratio(
        sum(
          views.filter((row) => row.offer_opportunity_name === opportunity),
          (row) => row.unique_views,
        ),
        sum(views, (row) => row.unique_views),
      ),
    };
  }
  const regionSummary = {
    current: region(filters.current, filters.from, filters.to),
    comparison: region(filters.comparison, filters.cmpFrom, filters.cmpTo),
  };
  const parityCvr = model.priceCurve.map((point) => {
    const date = new Date(Date.parse(filters.from + 'T00:00:00Z') + point.day * 86400000).toISOString().slice(0, 10);
    const rows = model.parity.current.rows.filter((row) => row.report_date === date);
    const denominator = sum(rows, (row) => row.total_views);
    const numerator = sum(rows, (row) => row.items_sold);
    return { ...point, cvr: denominator !== null && denominator > 0 ? (ratio(numerator, denominator) ?? 0) : null };
  });
  const channelNames = [
    ...new Set(
      [...model.current.viewRows, ...model.comparison.viewRows].map((row) => channelGroup(row.traffic_channel)),
    ),
  ].sort();
  const channelCurves = channelNames.map((name) => ({
    name,
    points: model.curve.map((point) => ({
      day: point.day,
      current: sum(
        model.current.viewRows.filter(
          (row) => channelGroup(row.traffic_channel) === name && days(filters.from, row.date) <= point.day,
        ),
        (row) => row.views,
      ),
      comparison: sum(
        model.comparison.viewRows.filter(
          (row) => channelGroup(row.traffic_channel) === name && days(filters.cmpFrom, row.date) <= point.day,
        ),
        (row) => row.views,
      ),
    })),
  }));
  return {
    checkinClassRows,
    packageMix: model.packageMix,
    guestMix,
    roomGuestMix,
    customerDetails,
    monthRows,
    roomShares,
    regionSummary,
    parityCvr,
    channelCurves,
  };
}
